import type { CreateGrnItemInput, GrnPrefillItem } from '@api/warehouse-ops';

/** One line of a finished-goods receipt: an item of the PO or transfer, and what arrived of it. */
export type FgLine = {
    itemId: string;
    itemName: string;
    sku: string | null;
    unit: string | null;
    /** What the PO ordered, or what the transfer dispatched. */
    ordered: number;
    /** As typed. Blank means nothing of this item arrived on this delivery. */
    quantity: string;
};

/** The source's lines, each waiting for what arrived - typed, or filled with "Add all remaining". */
export function buildFgLines(prefill: GrnPrefillItem[]): FgLine[] {
    return prefill.map((item) => ({
        itemId: item.itemId,
        itemName: item.itemName || item.itemId,
        sku: item.sku ?? null,
        unit: item.unit ?? null,
        ordered: Number(item.orderedQty) || 0,
        quantity: '',
    }));
}

/** Every line at what is still owed: the whole delivery arrived. */
export function fillAllRemaining(lines: FgLine[]): FgLine[] {
    return lines.map((line) => ({ ...line, quantity: String(line.ordered) }));
}

const parsed = (quantity: string): number | null => {
    const text = quantity.trim();
    if (text === '') return 0;
    const value = Number(text.replace(/,/g, ''));
    return Number.isFinite(value) ? value : null;
};

/** What is wrong with a line, in words; empty when it can be saved. */
export function fgLineIssues(line: FgLine): string[] {
    const value = parsed(line.quantity);
    if (value === null) return ['Quantity is not a number.'];
    if (value < 0) return ['Quantity cannot be below zero.'];
    return [];
}

/** Received minus ordered; null while nothing is typed. */
export function fgDifference(line: FgLine): number | null {
    if (line.quantity.trim() === '') return null;
    const value = parsed(line.quantity);
    return value === null ? null : value - line.ordered;
}

/** The lines the GRN carries: every line of the source, a blank one as nothing received. */
export function toGrnItems(lines: FgLine[]): CreateGrnItemInput[] {
    return lines.map((line) => ({
        itemId: line.itemId,
        orderedQty: line.ordered,
        receivedQty: parsed(line.quantity) ?? 0,
    }));
}

/** Something arrived and every line reads cleanly. */
export function canRecordFg(lines: FgLine[]): boolean {
    return lines.length > 0
        && lines.every((line) => fgLineIssues(line).length === 0)
        && lines.some((line) => (parsed(line.quantity) ?? 0) > 0);
}
